import { lazy, MouseEvent, Suspense, useCallback, useState } from 'react';

import { UserWithTokenModel } from '@picsart/growth-rc/types/models/user';
import { useAuthContext } from '@picsart/growth-rc/AuthContext';

import mapQrModal from 'strapi/componentMappings/mapQrModal';

import useModalsStore from 'stores/modalsStore';
import useMonetizationDataStore from 'stores/monetizationDataStore';

import { WithAuthModalProps } from 'redesignedComponents/decorators/withAuthModal';
import { LinkButtonProps } from 'redesignedComponents/molecules/CTA/LinkButton';

import useDevice from 'hooks/useDeviceType';

import ReddemErrorModal from 'components/MonetizationPopup/ReddemErrorModal';
import useCustomFlows from 'components/MonetizationPopup/useCustomFlows';
import usePageDataStore from 'stores/pageDataStore';
import usePageSlug from 'hooks/usePageSlug';
import useSubscriptionStore from 'stores/useSubscriptionStore';
import { LandingCategory } from 'constants/enums';
import useLandingPropsStore from 'stores/landingPropsStore';

const AuthModal = lazy(() => import(/* webpackChunkName: "AuthModal" */ 'components/AuthModal'));

function withAuthModal<T extends LinkButtonProps>(Component) {
  return function HOC(props: WithAuthModalProps<T>) {
    const { user } = useAuthContext();
    const { url, authorizationRequired, ignoreMonetization, ...restProps } = props;
    const [showAuthModal, setShowAuthModal] = useState(null);
    const { isDesktop } = useDevice();
    const monetizationPopupTrigger = useMonetizationDataStore(x => x.monetizationPopupTrigger);
    const needToOpenRegModal = usePageDataStore(x => x.needToOpenRegModal);
    const toggleQrModal = useModalsStore(store => store.toggleQrModal);
    const category = useLandingPropsStore(x => x.category);
    const isMaximizer = category === LandingCategory.maximizer;
    const { setRedirectTo } = useSubscriptionStore(store => ({
      setRedirectTo: store.setRedirectTo,
    }));
    const { slug: pagePath } = usePageSlug();
    const isHomePage = `/${pagePath}` === '/';
    const regFlowExperiment = needToOpenRegModal && isHomePage;
    const closeAuthModal = useCallback(() => {
      setShowAuthModal(false);
    }, []);
    const {
      customFlowInit,
      isErrorModalOpen,
      setIsErrorModalOpen,
      errMessage,
      isDiscord,
      isCustomWelcomePopup,
    } = useCustomFlows(true);

    const goToNextUrl = useCallback(
      (newUser: UserWithTokenModel) => {
        setShowAuthModal(null);
        if (isMaximizer) {
          setRedirectTo(url);
          return;
        }
        if (authorizationRequired && !ignoreMonetization && !regFlowExperiment) {
          if (isCustomWelcomePopup || isDiscord) {
            customFlowInit(newUser);
          } else {
            monetizationPopupTrigger(true);
          }
        } else if (regFlowExperiment) {
          if (newUser?.id) {
            window.location.href = '/create';
          }
        } else {
          window.location.href = url;
        }
      },
      [
        authorizationRequired,
        category,
        customFlowInit,
        ignoreMonetization,
        isCustomWelcomePopup,
        isDiscord,
        monetizationPopupTrigger,
        regFlowExperiment,
        setRedirectTo,
        url,
      ],
    );

    const handleOpenQrModalClick = useCallback(() => {
      if (restProps.qrModalTitle && !url && isDesktop) {
        toggleQrModal({
          isOpen: true,
          ...mapQrModal(restProps),
        });
      }
    }, [isDesktop, restProps, toggleQrModal, url]);

    const handleClick = useCallback(
      (e: MouseEvent) => {
        if (authorizationRequired || regFlowExperiment) {
          e.preventDefault();
          if (!user) {
            setShowAuthModal(true);
          } else if (!ignoreMonetization) {
            if (isCustomWelcomePopup || isDiscord) {
              customFlowInit();
            } else if (isMaximizer) {
              setRedirectTo(url);
            } else monetizationPopupTrigger(true);
          } else {
            goToNextUrl(user);
          }
        }
        handleOpenQrModalClick();
      },
      [
        authorizationRequired,
        customFlowInit,
        goToNextUrl,
        handleOpenQrModalClick,
        ignoreMonetization,
        isCustomWelcomePopup,
        isDiscord,
        monetizationPopupTrigger,
        regFlowExperiment,
        user,
      ],
    );

    return (
      <>
        <Component onClick={handleClick} {...props} />
        <Suspense fallback={null}>
          {showAuthModal !== null && (
            <AuthModal
              isOpen={showAuthModal}
              closeHandler={closeAuthModal}
              successHandler={goToNextUrl}
            />
          )}
          {isErrorModalOpen && (
            <ReddemErrorModal setIsModalOpen={setIsErrorModalOpen} errorMessage={errMessage} />
          )}
        </Suspense>
      </>
    );
  };
}

export default withAuthModal;
